import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, SHADOWS, FONTS } from '../../styles/tokens';
import { APP_ROLE_PREFIX, SYSTEM_ROLES } from '../../constants/auth-methods';
import {
  fetchUsers, fetchUserRoles, fetchRealmRoles, fetchClients, assignRoles,
  removeUserRole, updateUser, deleteUser,
  type KeycloakUser, type KeycloakRole,
} from '../../api/keycloak-admin';
import { recordAuditEvent } from '../../lib/notifications-api';
import { CreateUserModal } from '../../components/CreateUserModal';
import { UserRow } from '../../components/UserRow';
import { DeleteUserModal } from '../../components/DeleteUserModal';
import { ManageAppsModal, type AppOption } from '../../components/ManageAppsModal';
import { EditUserModal, type EditUserChanges } from '../../components/EditUserModal';

/** Full user management page: search, filters, app assignment, and account editing. */
export function UserManagement(): React.JSX.Element {
  const { token, username: selfUsername } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const [users, setUsers] = useState<KeycloakUser[]>([]);
  const [userRoles, setUserRoles] = useState<Record<string, string[]>>({});
  const [appRoles, setAppRoles] = useState<KeycloakRole[]>([]);
  const [allRoles, setAllRoles] = useState<KeycloakRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [appFilter, setAppFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [managingAppsUser, setManagingAppsUser] = useState<string | null>(null);
  const [appDescriptions, setAppDescriptions] = useState<Record<string, string>>({});

  const adminRole = allRoles.find(r => r.name === 'admin');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [allUsers, roles, clients] = await Promise.all([
        fetchUsers(token), fetchRealmRoles(token), fetchClients(token),
      ]);
      setUsers(allUsers);
      setAllRoles(roles);
      setAppRoles(roles.filter(r => r.name.startsWith(APP_ROLE_PREFIX)));

      const descBySlug: Record<string, string> = {};
      for (const c of clients) descBySlug[c.clientId] = c.description ?? '';
      setAppDescriptions(descBySlug);

      const roleMap: Record<string, string[]> = {};
      for (const user of allUsers) {
        const uRoles = await fetchUserRoles(token, user.id);
        roleMap[user.id] = uRoles.map(r => r.name);
      }
      setUserRoles(roleMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const adminCount = users.filter(u => (userRoles[u.id] ?? []).includes('admin')).length;

  /** Non-app, non-system realm roles available in the Role filter (besides admin). */
  const otherRoles = allRoles.filter(r =>
    !r.name.startsWith(APP_ROLE_PREFIX) && !SYSTEM_ROLES.has(r.name) && r.name !== 'admin',
  );

  const filteredUsers = users
    .filter(u => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return u.username.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.firstName?.toLowerCase().includes(q) ||
        u.lastName?.toLowerCase().includes(q);
    })
    .filter(u => !appFilter || (userRoles[u.id] ?? []).includes(appFilter))
    .filter(u => !roleFilter || (userRoles[u.id] ?? []).includes(roleFilter))
    .sort((a, b) => {
      const an = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim().toLowerCase();
      const bn = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim().toLowerCase();
      return sortDir === 'asc' ? an.localeCompare(bn) : bn.localeCompare(an);
    });

  async function handleDeleteUser(userId: string): Promise<void> {
    setActionInProgress(userId);
    try {
      await deleteUser(token, userId);
      setConfirmingDelete(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionInProgress(null);
    }
  }

  /** Commit a staged app-assignment diff as one operation (batched add + remove). */
  async function handleSaveApps(userId: string, addedNames: string[], removedNames: string[]): Promise<void> {
    setActionInProgress(userId);
    try {
      const roleByName = new Map(appRoles.map(r => [r.name, r]));
      const added = addedNames.map(n => roleByName.get(n)).filter((r): r is KeycloakRole => Boolean(r));
      const removed = removedNames.map(n => roleByName.get(n)).filter((r): r is KeycloakRole => Boolean(r));
      if (added.length > 0) await assignRoles(token, userId, added);
      if (removed.length > 0) await removeUserRole(token, userId, removed);

      const target = users.find(u => u.id === userId);
      const label = target ? (target.username || target.email) : userId;
      if (added.length > 0 || removed.length > 0) {
        const addedSlugs = added.map(r => r.name.replace(APP_ROLE_PREFIX, ''));
        const removedSlugs = removed.map(r => r.name.replace(APP_ROLE_PREFIX, ''));
        const parts: string[] = [];
        if (addedSlugs.length) parts.push(`+${addedSlugs.join(', ')}`);
        if (removedSlugs.length) parts.push(`-${removedSlugs.join(', ')}`);
        /* One audit entry per save carrying the full diff, not one per app. */
        await recordAuditEvent(token, {
          field: 'app-access',
          targetUserId: userId,
          targetLabel: label,
          summary: `Updated app access for ${label}`,
          detail: parts.join('  '),
          payload: { added: addedSlugs, removed: removedSlugs },
        }).catch(() => { /* best-effort audit */ });
      }

      setManagingAppsUser(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionInProgress(null);
    }
  }

  /** Commit staged identity, enabled, and admin-flag changes from the Edit user modal. */
  async function handleSaveUser(userId: string, changes: EditUserChanges): Promise<void> {
    setActionInProgress(userId);
    try {
      const target = users.find(u => u.id === userId);
      const wasAdmin = (userRoles[userId] ?? []).includes('admin');
      /* Guards mirror the modal, enforced again here before any write. */
      if (wasAdmin && !changes.makeAdmin) {
        if (target?.username === selfUsername) throw new Error('You cannot remove your own Vertex admin access.');
        if (adminCount <= 1) throw new Error('This is the last Vertex admin. Assign another admin first.');
      }

      await updateUser(token, userId, {
        firstName: changes.firstName,
        lastName: changes.lastName,
        email: changes.email,
        username: changes.username,
        enabled: changes.enabled,
      });

      if (adminRole) {
        if (changes.makeAdmin && !wasAdmin) await assignRoles(token, userId, [adminRole]);
        if (!changes.makeAdmin && wasAdmin) await removeUserRole(token, userId, [adminRole]);
      }

      /* Best-effort audit for the state changes made in this save. */
      const label = target ? (target.username || target.email) : userId;
      const wasEnabled = target?.enabled ?? true;
      const audits: Promise<void>[] = [];
      if (changes.enabled !== wasEnabled) {
        audits.push(recordAuditEvent(token, {
          field: 'account-enabled', targetUserId: userId, targetLabel: label,
          summary: `${changes.enabled ? 'Enabled' : 'Disabled'} ${label}`,
          payload: { from: wasEnabled, to: changes.enabled },
        }));
      }
      if (changes.makeAdmin !== wasAdmin) {
        audits.push(recordAuditEvent(token, {
          field: 'vertex-admin', targetUserId: userId, targetLabel: label,
          summary: `${changes.makeAdmin ? 'Granted' : 'Revoked'} Vertex admin ${changes.makeAdmin ? 'to' : 'from'} ${label}`,
          payload: { from: wasAdmin, to: changes.makeAdmin },
        }));
      }
      await Promise.allSettled(audits);

      setEditingUser(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionInProgress(null);
    }
  }

  const deletingUser = confirmingDelete ? users.find(u => u.id === confirmingDelete) : null;
  const managingUser = managingAppsUser ? users.find(u => u.id === managingAppsUser) : null;
  const editUser = editingUser ? users.find(u => u.id === editingUser) : null;

  /** All assignable apps as modal options, with descriptions from their client records. */
  const appOptions: AppOption[] = appRoles.map(r => {
    const slug = r.name.replace(APP_ROLE_PREFIX, '');
    return { roleName: r.name, slug, description: appDescriptions[slug] ?? '' };
  });

  const filterSelectStyle: React.CSSProperties = {
    height: '34px', padding: '0 10px', borderRadius: '6px',
    border: `1px solid ${colors.border}`, backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
    color: colors.text, fontFamily: FONTS.body, fontSize: '12px', outline: 'none', cursor: 'pointer',
  };

  const headerCellStyle: React.CSSProperties = {
    fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase',
    color: isDark ? '#D0D0D0' : '#333333',
  };

  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: 0 }}>
          User Management
        </h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search users"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="vx-search-field"
            style={{
              height: '34px', padding: '0 12px', borderRadius: '6px',
              border: `1px solid ${colors.border}`, backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
              color: colors.text, fontFamily: FONTS.body, fontSize: '12px', outline: 'none', width: '190px',
            }}
          />
          <select className="vx-full-field" value={appFilter} onChange={e => setAppFilter(e.target.value)} style={filterSelectStyle} aria-label="Filter by app">
            <option value="">All apps</option>
            {[...appRoles].sort((a, b) => a.name.localeCompare(b.name)).map(r => (
              <option key={r.name} value={r.name}>{r.name.replace(APP_ROLE_PREFIX, '')}</option>
            ))}
          </select>
          <select className="vx-full-field" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={filterSelectStyle} aria-label="Filter by role">
            <option value="">All roles</option>
            <option value="admin">Vertex admin</option>
            {otherRoles.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
          </select>
          <button onClick={() => setShowCreateModal(true)} style={{
            height: '34px', padding: '0 16px', borderRadius: '6px', border: 'none',
            background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
            color: '#FFFFFF', fontFamily: FONTS.heading, fontSize: '10px',
            letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer',
          }}>+ New user</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: isDark ? '#3b1c1c' : '#fde8e8', border: '1px solid #EF4444', borderRadius: '8px', color: '#EF4444', fontFamily: FONTS.body, fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading users...</p>
      ) : (
        <div style={{ backgroundColor: colors.card, borderRadius: '12px', boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight, overflow: 'hidden' }}>
          {/* Table header */}
          <div className="vx-desktop-header" style={{ display: 'grid', gridTemplateColumns: '215px 1fr', gap: '16px', padding: '12px 20px', borderBottom: `1px solid ${colors.border}` }}>
            <button
              onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
              style={{ ...headerCellStyle, background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', textAlign: 'left' }}
            >
              User
              <span aria-hidden="true" style={{ fontSize: '9px' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
            </button>
            <span style={headerCellStyle}>Apps</span>
          </div>

          {/* User rows */}
          {filteredUsers.map(user => (
            <UserRow
              key={user.id}
              user={user}
              isDark={isDark}
              colors={colors}
              roles={userRoles[user.id] ?? []}
              isProcessing={actionInProgress === user.id}
              onManageApps={() => setManagingAppsUser(user.id)}
              onEdit={() => setEditingUser(user.id)}
            />
          ))}

          {filteredUsers.length === 0 && (
            <div style={{ padding: '32px', textAlign: 'center', fontFamily: FONTS.body, fontSize: '14px', color: colors.textSecondary }}>
              No users found
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmingDelete && deletingUser && (
        <DeleteUserModal
          user={deletingUser}
          isDark={isDark}
          colors={colors}
          isDeleting={actionInProgress === confirmingDelete}
          onConfirm={() => handleDeleteUser(confirmingDelete)}
          onCancel={() => setConfirmingDelete(null)}
        />
      )}

      {showCreateModal && (
        <CreateUserModal
          appRoles={appRoles}
          allRoles={allRoles}
          onCreated={() => { setShowCreateModal(false); loadData(); }}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {managingAppsUser && managingUser && (
        <ManageAppsModal
          user={managingUser}
          apps={appOptions}
          assignedRoleNames={(userRoles[managingAppsUser] ?? []).filter(r => r.startsWith(APP_ROLE_PREFIX))}
          isDark={isDark}
          colors={colors}
          isSaving={actionInProgress === managingAppsUser}
          onSave={(added, removed) => handleSaveApps(managingAppsUser, added, removed)}
          onClose={() => setManagingAppsUser(null)}
        />
      )}

      {editingUser && editUser && (
        <EditUserModal
          user={editUser}
          isAdmin={(userRoles[editingUser] ?? []).includes('admin')}
          isSelf={editUser.username === selfUsername}
          isLastAdmin={(userRoles[editingUser] ?? []).includes('admin') && adminCount <= 1}
          isDark={isDark}
          colors={colors}
          isSaving={actionInProgress === editingUser}
          onSave={changes => handleSaveUser(editingUser, changes)}
          onDelete={() => { setConfirmingDelete(editingUser); setEditingUser(null); }}
          onClose={() => setEditingUser(null)}
        />
      )}
    </div>
  );
}
